import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Phone, Clock, MessageSquare, AlertCircle } from "lucide-react";

/** Forwarding rules configuration structure */
export interface ForwardingRules {
  type?: 'simple' | 'sequential' | 'simultaneous';
  numbers?: string[];
  timeout_seconds?: number;
  voicemail_enabled?: boolean;
  voicemail_greeting?: string;
  business_hours?: {
    enabled: boolean;
    timezone: string;
    after_hours_action?: 'voicemail' | 'forward' | 'message';
    after_hours_number?: string;
    after_hours_message?: string;
    schedule?: Record<string, { start: string; end: string }>;
  };
}

interface ForwardingRulesEditorProps {
  campaignId: string;
  currentRules?: ForwardingRules;
  simpleForwardingNumber?: string | null;
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const TIMEZONES = [
  'America/New_York',
  'America/Chicago', 
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Vancouver',
  'Europe/London',
  'Europe/Paris',
  'Australia/Sydney',
  'Asia/Tokyo',
  'UTC',
];

/**
 * Validates E.164 phone number format
 * @param phone - Phone number to validate (strips spaces, parens, dashes)
 * @returns true if valid E.164 format (+[country code][number], 7-15 digits)
 */
function isValidE164(phone: string): boolean {
  if (!phone) return false;
  const cleaned = phone.replace(/[\s()\-]/g, '');
  return /^\+[1-9]\d{6,14}$/.test(cleaned);
}

/**
 * Normalize phone number to E.164 format
 */
function normalizePhone(phone: string): string {
  return phone.replace(/[\s()\-]/g, '');
}

export function ForwardingRulesEditor({ campaignId, currentRules, simpleForwardingNumber }: ForwardingRulesEditorProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Initialize state from props
  const [rules, setRules] = useState<ForwardingRules>(() => ({
    type: currentRules?.type || 'simple',
    numbers: currentRules?.numbers || (simpleForwardingNumber ? [simpleForwardingNumber] : []),
    timeout_seconds: currentRules?.timeout_seconds || 25,
    voicemail_enabled: currentRules?.voicemail_enabled ?? true,
    voicemail_greeting: currentRules?.voicemail_greeting || 'Thank you for calling. Please leave a message after the beep.',
    business_hours: currentRules?.business_hours || {
      enabled: false,
      timezone: 'America/New_York',
      after_hours_action: 'voicemail',
      after_hours_number: '',
      after_hours_message: 'We are currently closed. Please leave a message.',
      schedule: {
        monday: { start: '09:00', end: '17:00' },
        tuesday: { start: '09:00', end: '17:00' },
        wednesday: { start: '09:00', end: '17:00' },
        thursday: { start: '09:00', end: '17:00' },
        friday: { start: '09:00', end: '17:00' },
        saturday: { start: '', end: '' },
        sunday: { start: '', end: '' },
      },
    },
  }));

  // Update rules when props change
  useEffect(() => {
    if (currentRules) {
      setRules(prev => ({
        ...prev,
        ...currentRules,
        numbers: currentRules.numbers || (simpleForwardingNumber ? [simpleForwardingNumber] : prev.numbers),
      }));
    }
  }, [currentRules, simpleForwardingNumber]);

  // Validate rules before saving
  const validateRules = (): string[] => {
    const errors: string[] = [];
    
    // Check for empty numbers array for sequential/simultaneous
    if ((rules.type === 'sequential' || rules.type === 'simultaneous') && 
        (!rules.numbers || rules.numbers.length === 0)) {
      errors.push('At least one forwarding number is required for sequential/simultaneous ring.');
    }
    
    // Validate all phone numbers are E.164 format
    rules.numbers?.forEach((num, idx) => {
      if (num && !isValidE164(num)) {
        errors.push(`Number ${idx + 1} is not valid E.164 format (e.g., +15551234567).`);
      }
    });
    
    // Validate after-hours number if forward action selected
    if (rules.business_hours?.enabled && 
        rules.business_hours.after_hours_action === 'forward' &&
        rules.business_hours.after_hours_number &&
        !isValidE164(rules.business_hours.after_hours_number)) {
      errors.push('After-hours forwarding number is not valid E.164 format.');
    }
    
    return errors;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Validate before saving
      const errors = validateRules();
      if (errors.length > 0) {
        setValidationErrors(errors);
        throw new Error('Validation failed');
      }
      setValidationErrors([]);
      
      // Normalize phone numbers before saving
      const normalizedRules: ForwardingRules = {
        ...rules,
        numbers: rules.numbers?.map(normalizePhone).filter(Boolean),
        business_hours: rules.business_hours ? {
          ...rules.business_hours,
          after_hours_number: rules.business_hours.after_hours_number 
            ? normalizePhone(rules.business_hours.after_hours_number)
            : undefined,
        } : undefined,
      };
      
      // Save both forwarding_number (for simple backward compat) and forwarding_rules
      const primaryNumber = normalizedRules.numbers?.[0] || null;
      
      const { error } = await supabase
        .from('calie_campaigns')
        .update({
          forwarding_number: primaryNumber,
          forwarding_rules: JSON.parse(JSON.stringify(normalizedRules)),
        })
        .eq('campaign_id', campaignId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Forwarding rules saved' });
      qc.invalidateQueries({ queryKey: ['campaign', campaignId] });
    },
    onError: (e: Error) => {
      if (e.message !== 'Validation failed') {
        toast({ title: 'Failed to save', description: e.message, variant: 'destructive' });
      }
    },
  });

  const addNumber = () => {
    setRules(prev => ({
      ...prev,
      numbers: [...(prev.numbers || []), ''],
    }));
  };

  const removeNumber = (index: number) => {
    // Prevent removing the last number for sequential/simultaneous
    if ((rules.type === 'sequential' || rules.type === 'simultaneous') && 
        (rules.numbers?.length || 0) <= 1) {
      return;
    }
    setRules(prev => ({
      ...prev,
      numbers: (prev.numbers || []).filter((_, i) => i !== index),
    }));
  };

  const updateNumber = (index: number, value: string) => {
    setRules(prev => ({
      ...prev,
      numbers: (prev.numbers || []).map((n, i) => i === index ? value : n),
    }));
  };

  const updateSchedule = (day: string, field: 'start' | 'end', value: string) => {
    setRules(prev => ({
      ...prev,
      business_hours: {
        ...prev.business_hours!,
        schedule: {
          ...prev.business_hours!.schedule,
          [day]: {
            ...prev.business_hours!.schedule![day],
            [field]: value,
          },
        },
      },
    }));
  };

  // Check if a number is valid for inline feedback
  const getNumberValidation = (number: string): { valid: boolean; message?: string } => {
    if (!number) return { valid: true }; // Empty is okay, will be filtered on save
    if (isValidE164(number)) return { valid: true };
    return { valid: false, message: 'Format: +15551234567' };
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Phone className="h-5 w-5" />
          Call Forwarding Rules
        </CardTitle>
        <CardDescription>
          Configure how incoming calls are routed for this campaign
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Validation Errors */}
        {validationErrors.length > 0 && (
          <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3 space-y-1">
            <div className="flex items-center gap-2 text-destructive font-medium">
              <AlertCircle className="h-4 w-4" />
              Please fix the following errors:
            </div>
            <ul className="list-disc list-inside text-sm text-destructive">
              {validationErrors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Forwarding Type */}
        <div className="space-y-2">
          <Label>Forwarding Type</Label>
          <Select
            value={rules.type}
            onValueChange={(v) => setRules(prev => ({ ...prev, type: v as ForwardingRules['type'] }))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="simple">Simple (Single Number)</SelectItem>
              <SelectItem value="sequential">Sequential Ring (Try numbers in order)</SelectItem>
              <SelectItem value="simultaneous">Simultaneous Ring (All at once)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {rules.type === 'sequential' && 'Calls will try each number in order until answered.'}
            {rules.type === 'simultaneous' && 'All numbers will ring at the same time.'}
            {rules.type === 'simple' && 'Calls forward to a single number.'}
          </p>
        </div>

        {/* Phone Numbers */}
        <div className="space-y-2">
          <Label>Forwarding Numbers</Label>
          {(rules.numbers || []).map((number, index) => {
            const validation = getNumberValidation(number);
            return (
              <div key={index} className="space-y-1">
                <div className="flex gap-2">
                  <Input
                    placeholder="+15551234567"
                    value={number}
                    onChange={(e) => updateNumber(index, e.target.value)}
                    className={`flex-1 ${!validation.valid ? 'border-destructive' : ''}`}
                  />
                  {/* Only show remove button if more than 1 number OR type is simple */}
                  {((rules.numbers || []).length > 1 || rules.type === 'simple') && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeNumber(index)}
                      disabled={(rules.type === 'sequential' || rules.type === 'simultaneous') && (rules.numbers?.length || 0) <= 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {!validation.valid && (
                  <p className="text-xs text-destructive">{validation.message}</p>
                )}
              </div>
            );
          })}
          {(rules.type === 'sequential' || rules.type === 'simultaneous') && (
            <Button type="button" variant="outline" size="sm" onClick={addNumber}>
              <Plus className="h-4 w-4 mr-1" /> Add Number
            </Button>
          )}
          <p className="text-xs text-muted-foreground">
            Use E.164 format: +[country code][number] (e.g., +15551234567)
          </p>
        </div>

        {/* Ring Timeout */}
        <div className="space-y-2">
          <Label>Ring Timeout (seconds)</Label>
          <Input
            type="number"
            min={5}
            max={60}
            value={rules.timeout_seconds}
            onChange={(e) => setRules(prev => ({ ...prev, timeout_seconds: parseInt(e.target.value) || 25 }))}
            className="w-32"
          />
          <p className="text-xs text-muted-foreground">
            How long to ring before moving to next number or voicemail (5-60 seconds)
          </p>
        </div>

        {/* Voicemail Settings */}
        <div className="space-y-4 border-t pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              <Label>Enable Voicemail</Label>
            </div>
            <Switch
              checked={rules.voicemail_enabled}
              onCheckedChange={(v) => setRules(prev => ({ ...prev, voicemail_enabled: v }))}
            />
          </div>
          {rules.voicemail_enabled && (
            <div className="space-y-2">
              <Label>Voicemail Greeting</Label>
              <Textarea
                value={rules.voicemail_greeting}
                onChange={(e) => setRules(prev => ({ ...prev, voicemail_greeting: e.target.value }))}
                placeholder="Thank you for calling. Please leave a message after the beep."
                rows={2}
              />
            </div>
          )}
        </div>

        {/* Business Hours */}
        <div className="space-y-4 border-t pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <Label>Business Hours Routing</Label>
            </div>
            <Switch
              checked={rules.business_hours?.enabled || false}
              onCheckedChange={(v) => setRules(prev => ({
                ...prev,
                business_hours: { ...prev.business_hours!, enabled: v },
              }))}
            />
          </div>

          {rules.business_hours?.enabled && (
            <div className="space-y-4 pl-4 border-l-2 border-muted">
              {/* Timezone */}
              <div className="space-y-2">
                <Label>Timezone</Label>
                <Select
                  value={rules.business_hours.timezone}
                  onValueChange={(v) => setRules(prev => ({
                    ...prev,
                    business_hours: { ...prev.business_hours!, timezone: v },
                  }))}
                >
                  <SelectTrigger className="w-full sm:w-64">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map(tz => (
                      <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Schedule - Responsive Grid */}
              <div className="space-y-2">
                <Label>Schedule</Label>
                <div className="grid gap-2">
                  {DAYS.map(day => (
                    <div key={day} className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-center">
                      <span className="text-sm capitalize col-span-2 sm:col-span-1">{day}</span>
                      <Input
                        type="time"
                        value={rules.business_hours!.schedule?.[day]?.start || ''}
                        onChange={(e) => updateSchedule(day, 'start', e.target.value)}
                        className="text-sm"
                      />
                      <span className="hidden sm:block text-center text-muted-foreground">to</span>
                      <Input
                        type="time"
                        value={rules.business_hours!.schedule?.[day]?.end || ''}
                        onChange={(e) => updateSchedule(day, 'end', e.target.value)}
                        className="text-sm"
                      />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">Leave empty for closed days</p>
              </div>

              {/* After Hours Action */}
              <div className="space-y-2">
                <Label>After Hours Action</Label>
                <Select
                  value={rules.business_hours.after_hours_action}
                  onValueChange={(v) => setRules(prev => ({
                    ...prev,
                    business_hours: { ...prev.business_hours!, after_hours_action: v as 'voicemail' | 'forward' | 'message' },
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="voicemail">Send to Voicemail</SelectItem>
                    <SelectItem value="forward">Forward to Different Number</SelectItem>
                    <SelectItem value="message">Play Message & Hang Up</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {rules.business_hours.after_hours_action === 'forward' && (
                <div className="space-y-2">
                  <Label>After Hours Number</Label>
                  <Input
                    placeholder="+15559990000"
                    value={rules.business_hours.after_hours_number || ''}
                    onChange={(e) => setRules(prev => ({
                      ...prev,
                      business_hours: { ...prev.business_hours!, after_hours_number: e.target.value },
                    }))}
                    className={rules.business_hours.after_hours_number && !isValidE164(rules.business_hours.after_hours_number) ? 'border-destructive' : ''}
                  />
                  {rules.business_hours.after_hours_number && !isValidE164(rules.business_hours.after_hours_number) && (
                    <p className="text-xs text-destructive">Format: +15551234567</p>
                  )}
                </div>
              )}

              {(rules.business_hours.after_hours_action === 'message' || rules.business_hours.after_hours_action === 'voicemail') && (
                <div className="space-y-2">
                  <Label>After Hours Message</Label>
                  <Textarea
                    value={rules.business_hours.after_hours_message || ''}
                    onChange={(e) => setRules(prev => ({
                      ...prev,
                      business_hours: { ...prev.business_hours!, after_hours_message: e.target.value },
                    }))}
                    placeholder="We are currently closed. Please call back during business hours."
                    rows={2}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t">
          <Button 
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving...' : 'Save Forwarding Rules'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
